"use client";

import { useCallback, useMemo, useState } from "react";
import { useActivityLogFilters } from "@/hooks/use-activity-log-filters";
import { useActivityLogListQuery } from "@/lib/queries/activity-log";
import { ActivityLogFilters } from "./activity-log/activity-log-filters";
import { ActivityLogList } from "./activity-log/activity-log-list";
import { ActivityLogDetailSheet } from "./activity-log/activity-log-detail-sheet";
import { Card, CardContent } from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertCircleIcon } from "lucide-react";
import type { LogEventViewModel } from "@/types/activity-log";

function AdministrationActivity() {
  const { filters, setFilters, paginationSizes } = useActivityLogFilters();

  const query = useActivityLogListQuery(
    {
      dateFrom: filters.dateFrom ?? undefined,
      dateTo: filters.dateTo ?? undefined,
      path: filters.path ?? undefined,
      kvps: filters.kvps ?? undefined,
      actionType: filters.actionType ?? undefined,
      result: filters.result ?? undefined,
      q: filters.q ?? undefined,
      p: filters.p,
      s: filters.s,
    },
    undefined,
    undefined,
  );

  const items: LogEventViewModel[] = useMemo(
    () =>
      (query.data?.items ?? []).map((i) => ({
        ...(i as Omit<LogEventViewModel, "createdAt"> & { createdAt: string }),
        createdAt: new Date(i.createdAt),
      })),
    [query.data?.items],
  );

  const totalCount = query.data?.totalCount ?? 0;
  const distinctKvps = query.data?.distinctKvps ?? [];

  const [selectedItem, setSelectedItem] = useState<LogEventViewModel | null>(
    null,
  );
  const [detailOpen, setDetailOpen] = useState(false);

  const handleOpenDetail = useCallback(
    (id: number) => {
      const item = items.find((i) => i.id === id);
      if (item) {
        setSelectedItem(item);
        setDetailOpen(true);
      }
    },
    [items],
  );

  const handlePageChange = useCallback(
    (params: { page: number; size: number }) => {
      setFilters({ p: params.page, s: params.size });
    },
    [setFilters],
  );

  if (query.error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-8">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon" className="bg-destructive/10">
                <AlertCircleIcon className="text-destructive size-6" />
              </EmptyMedia>
              <EmptyTitle>Erreur de chargement</EmptyTitle>
              <EmptyDescription>
                Une erreur est survenue lors du chargement. Veuillez réessayer.
              </EmptyDescription>
            </EmptyHeader>
            <Button variant="outline" onClick={() => query.refetch()}>
              Réessayer
            </Button>
          </Empty>
        </CardContent>
      </Card>
    );
  }

  const isLoading = query.isPending;

  return (
    <div className="space-y-6">
      <ActivityLogFilters distinctKvps={distinctKvps} />
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <Empty className="border border-dashed">
              <EmptyHeader>
                <EmptyMedia variant="icon" />
                <EmptyTitle>Aucun événement</EmptyTitle>
                <EmptyDescription>
                  Aucun journal d&apos;activité ne correspond aux filtres.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </CardContent>
        </Card>
      ) : (
        <ActivityLogList
          items={items}
          totalCount={totalCount}
          page={filters.p}
          pageSize={filters.s}
          paginationSizes={paginationSizes}
          onPageChange={handlePageChange}
          onOpenDetail={handleOpenDetail}
        />
      )}
      <ActivityLogDetailSheet
        item={selectedItem}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  );
}

export { AdministrationActivity };
